import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import {
    MdOutlineHome,
    MdOutlineBusinessCenter,
    MdOutlineRocketLaunch,
    MdOutlinePersonOutline,
    MdOutlineAutoGraph,
    MdOutlineBuild,
    MdOutlineForum,
    MdOutlineMailOutline
} from 'react-icons/md';

const SideNavigation = ({ isMenuOpen }) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [activeSection, setActiveSection] = useState('home');
    const isScrollingRef = React.useRef(false);

    const navItems = [
        { icon: MdOutlineHome, label: 'Home', href: '#home', id: 'home' },
        { icon: MdOutlinePersonOutline, label: 'About Me', href: '#insights', id: 'insights' },
        { icon: MdOutlineBusinessCenter, label: 'Experience', href: '#works', id: 'works' },
        { icon: MdOutlineRocketLaunch, label: 'Projects', href: '#projects', id: 'projects' },

        // { icon: MdOutlineAutoGraph, label: 'Insights', href: '#insights', id: 'insights' },
        { icon: MdOutlineBuild, label: 'Skills', href: '#stack', id: 'stack' },
        { icon: MdOutlineForum, label: 'Testimonials', href: '#testimonials', id: 'testimonials' },
        { icon: MdOutlineMailOutline, label: 'Contact', href: '#contact', id: 'contact' },
    ];

    useEffect(() => {
        const handleScrollSpy = () => {
            if (isScrollingRef.current) return;

            const sections = navItems.map(item => ({
                id: item.id,
                element: document.querySelector(item.href)
            }));

            const scrollPosition = window.scrollY + window.innerHeight / 3;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (section.element && scrollPosition >= section.element.offsetTop) {
                    if (activeSection !== section.id) {
                        setActiveSection(section.id);
                    }
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScrollSpy, { passive: true });
        return () => window.removeEventListener('scroll', handleScrollSpy);
    }, [activeSection, navItems]);

    const handleScroll = useCallback((href, id) => {
        const element = document.querySelector(href);
        if (element) {
            isScrollingRef.current = true;
            setActiveSection(id);

            const elementTop = element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: elementTop - 100,
                behavior: 'smooth',
            });

            setTimeout(() => {
                isScrollingRef.current = false;
            }, 800);
        }
    }, [activeSection]);

    const springTransition = {
        type: "spring",
        stiffness: 500,
        damping: 35,
        mass: 1,
    };

    return (
        <AnimatePresence>
            {!isMenuOpen && (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.8, ease: "circOut" }}
                    className="fixed left-6 top-0 bottom-0 items-center z-50 hidden xl:flex"
                >
                    <div className="relative p-2 bg-white/5 dark:bg-black/20 backdrop-blur-xl border border-zinc-500/30 dark:border-white/20 rounded-full ">
                        <div className="flex flex-col gap-3 relative">
                            {navItems.map((item, index) => {
                                const Icon = item.icon;
                                const isActive = activeSection === item.id;
                                const isHovered = hoveredIndex === index;

                                return (
                                    <div
                                        key={item.id}
                                        className="relative flex items-center justify-center w-10 h-10"
                                        onMouseEnter={() => setHoveredIndex(index)}
                                        onMouseLeave={() => setHoveredIndex(null)}
                                    >
                                        {/* Active Capsule background */}
                                        {isActive && (
                                            <motion.div
                                                layoutId="active-nav-bg"
                                                className="absolute inset-0 bg-zinc-900/10 dark:bg-white/15 rounded-full pointer-events-none"
                                                transition={springTransition}
                                                style={{ willChange: 'transform, opacity' }}
                                            />
                                        )}

                                        {/* Premium Active Indicator Line (Left) */}
                                        {isActive && (
                                            <motion.div
                                                layoutId="active-nav-line"
                                                className="absolute -left-4 w-1 h-5 bg-zinc-900 dark:bg-white rounded-full shadow-[0_0_12px_rgba(255,255,255,0.5)]"
                                                transition={springTransition}
                                                style={{ willChange: 'transform' }}
                                            />
                                        )}

                                        <motion.button
                                            onClick={() => handleScroll(item.href, item.id)}
                                            className={`relative z-10 transition-colors duration-200 ${isActive ? 'text-zinc-950 dark:text-white' : 'text-zinc-400 dark:text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200'
                                                }`}
                                        >
                                            <Icon size={20} className={isActive ? "scale-110" : ""} />
                                        </motion.button>

                                        {/* Tooltip */}
                                        <AnimatePresence>
                                            {isHovered && (
                                                <motion.div
                                                    initial={{ opacity: 0, x: 10, filter: 'blur(4px)' }}
                                                    animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                                                    exit={{ opacity: 0, x: 10, filter: 'blur(4px)' }}
                                                    className="absolute left-full ml-4 pointer-events-none"
                                                >
                                                    <div className="bg-zinc-900/90 dark:bg-white/95 backdrop-blur-md text-white dark:text-zinc-900 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap shadow-xl border border-white/10 dark:border-black/10">
                                                        {item.label}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SideNavigation;
